import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { RouteQueryComposable as SmsRouteQueryComposable } from '@/composables/routeQueries/useSmsRouteQuery';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import {
  fetchGetSmsMessagesConversations,
  fetchGetSmsMessagesHistory,
  fetchPostSmsMessagesMarkRead,
  fetchPostSmsMessagesSend,
} from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface SmsChatViewDataComposable {
  isFetchingConversationsData: Readonly<Ref<boolean>>;
  isFetchingMessageHistoryData: Readonly<Ref<boolean>>;
  isFetchingMarkReadData: Readonly<Ref<boolean>>;
  isFetchingSendData: Readonly<Ref<boolean>>;

  conversationsData: Readonly<
    Ref<ReadonlyDeep<openapi.GetSmsMessagesConversations_200> | undefined>
  >;
  messageHistoryData: Readonly<
    Ref<ReadonlyDeep<openapi.GetSmsMessagesHistory_200> | undefined>
  >;
  markReadData: Readonly<
    Ref<ReadonlyDeep<openapi.PostSmsMessagesMarkRead_200> | undefined>
  >;
  sendData: Readonly<
    Ref<ReadonlyDeep<openapi.PostSmsMessagesSend_200> | undefined>
  >;

  conversationsDataError: Readonly<Ref<unknown | undefined>>;
  messageHistoryDataError: Readonly<Ref<unknown | undefined>>;
  markReadDataError: Readonly<Ref<unknown | undefined>>;
  sendDataError: Readonly<Ref<unknown | undefined>>;

  refreshConversationsData: () => Promise<void>;
  refreshMessageHistoryData: () => Promise<void>;
  refreshMarkReadData: (
    body: ReadonlyDeep<openapi.PostSmsMessagesMarkRead_Body>,
  ) => Promise<void>;
  refreshSendData: (
    body: ReadonlyDeep<openapi.PostSmsMessagesSend_Body>,
  ) => Promise<void>;

  reload: () => SmsChatViewDataComposable;
}

export default function useSmsChatViewData(
  routeQuery: SmsRouteQueryComposable,
): SmsChatViewDataComposable {
  const route = useRoute();

  const loaderStore = useLoaderStore();

  let conversationsDataController: AbortController | undefined;
  let messageHistoryDataController: AbortController | undefined;
  let markReadDataController: AbortController | undefined;
  let sendDataController: AbortController | undefined;

  const conversationsDataFetchCounter = ref(0);
  const messageHistoryDataFetchCounter = ref(0);
  const markReadDataFetchCounter = ref(0);
  const sendDataFetchCounter = ref(0);

  const isFetchingConversationsData = computed(
    () => !!conversationsDataFetchCounter.value,
  );
  const isFetchingMessageHistoryData = computed(
    () => !!messageHistoryDataFetchCounter.value,
  );
  const isFetchingMarkReadData = computed(
    () => !!markReadDataFetchCounter.value,
  );
  const isFetchingSendData = computed(() => !!sendDataFetchCounter.value);

  const conversationsData =
    ref<ReadonlyDeep<openapi.GetSmsMessagesConversations_200>>();
  const messageHistoryData =
    ref<ReadonlyDeep<openapi.GetSmsMessagesHistory_200>>();
  const markReadData = ref<ReadonlyDeep<openapi.PostSmsMessagesMarkRead_200>>();
  const sendData = ref<ReadonlyDeep<openapi.PostSmsMessagesSend_200>>();

  const conversationsDataError = ref();
  const messageHistoryDataError = ref();
  const markReadDataError = ref();
  const sendDataError = ref();

  function clear(): void {
    if (conversationsDataController) conversationsDataController.abort();
    if (messageHistoryDataController) messageHistoryDataController.abort();
    if (markReadDataController) markReadDataController.abort();
    if (sendDataController) sendDataController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchConversationsData(): Promise<
    ReadonlyDeep<openapi.GetSmsMessagesConversations_200> | undefined
  > {
    if (conversationsDataController) conversationsDataController.abort();
    conversationsDataController = new AbortController();
    return await fetchGetSmsMessagesConversations(undefined, {
      signal: conversationsDataController.signal,
    })
      .then(function (data) {
        conversationsDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        conversationsDataError.value = err;
        throw err;
      });
  }

  async function _fetchMessageHistoryData(): Promise<
    ReadonlyDeep<openapi.GetSmsMessagesHistory_200> | undefined
  > {
    if (messageHistoryDataController) messageHistoryDataController.abort();
    if (!routeQuery.recipient.value) return;
    messageHistoryDataController = new AbortController();
    return await fetchGetSmsMessagesHistory(
      {
        driver: routeQuery.recipient.value,
      },
      {
        signal: messageHistoryDataController.signal,
      },
    )
      .then(function (data) {
        messageHistoryDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        messageHistoryDataError.value = err;
        throw err;
      });
  }

  async function _fetchMarkReadData(
    body: ReadonlyDeep<openapi.PostSmsMessagesMarkRead_Body> | undefined,
  ): Promise<ReadonlyDeep<openapi.PostSmsMessagesMarkRead_200> | undefined> {
    if (markReadDataController) markReadDataController.abort();
    if (!body) return;
    markReadDataController = new AbortController();
    return await fetchPostSmsMessagesMarkRead(undefined, body, {
      signal: markReadDataController.signal,
    })
      .then(function (data) {
        markReadDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        markReadDataError.value = err;
        throw err;
      });
  }

  async function _fetchSendData(
    body: ReadonlyDeep<openapi.PostSmsMessagesSend_Body> | undefined,
  ): Promise<ReadonlyDeep<openapi.PostSmsMessagesSend_200> | undefined> {
    if (sendDataController) sendDataController.abort();
    if (!body) return;
    sendDataController = new AbortController();
    return await fetchPostSmsMessagesSend(undefined, body, {
      signal: sendDataController.signal,
    })
      .then(function (data) {
        sendDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        sendDataError.value = err;
        throw err;
      });
  }

  async function refreshConversationsData(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    conversationsDataFetchCounter.value++;
    try {
      conversationsData.value = await _fetchConversationsData();
    } finally {
      conversationsDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshMessageHistoryData(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    messageHistoryDataFetchCounter.value++;
    try {
      messageHistoryData.value = await _fetchMessageHistoryData();
    } finally {
      messageHistoryDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshMarkReadData(
    body: ReadonlyDeep<openapi.PostSmsMessagesMarkRead_Body>,
  ): Promise<void> {
    loaderStore.increaseGlobalLoader();
    markReadDataFetchCounter.value++;
    try {
      markReadData.value = await _fetchMarkReadData(body);
    } finally {
      markReadDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshSendData(
    body: ReadonlyDeep<openapi.PostSmsMessagesSend_Body>,
  ): Promise<void> {
    loaderStore.increaseGlobalLoader();
    sendDataFetchCounter.value++;
    try {
      sendData.value = await _fetchSendData(body);
    } finally {
      sendDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  function reload(): SmsChatViewDataComposable {
    void Promise.all([refreshConversationsData(), refreshMessageHistoryData()]);
    return {
      isFetchingConversationsData,
      isFetchingMessageHistoryData,
      isFetchingMarkReadData,
      isFetchingSendData,
      conversationsData,
      messageHistoryData,
      markReadData,
      sendData,
      conversationsDataError,
      messageHistoryDataError,
      markReadDataError,
      sendDataError,
      refreshConversationsData,
      refreshMessageHistoryData,
      refreshMarkReadData,
      refreshSendData,
      reload,
    };
  }

  watch(
    [routeQuery.recipient],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.SMS_CHAT) return;
      if (!conversationsData.value) {
        reload();
      } else {
        void refreshMessageHistoryData();
      }
    },
    { immediate: true },
  );

  return {
    isFetchingConversationsData,
    isFetchingMessageHistoryData,
    isFetchingMarkReadData,
    isFetchingSendData,
    conversationsData,
    messageHistoryData,
    markReadData,
    sendData,
    conversationsDataError,
    messageHistoryDataError,
    markReadDataError,
    sendDataError,
    refreshConversationsData,
    refreshMessageHistoryData,
    refreshMarkReadData,
    refreshSendData,
    reload,
  };
}
